import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  Dialog,
  FormControl,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Logo from '../../assets/images/logo.svg';
import WhiteLogo from '../../assets/images/logo-white.svg';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Fade from '@mui/material/Fade';
import { useAuth0 } from '@auth0/auth0-react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLogout } from '../../hooks/useLogout';
import { CustomColors } from '../../theme';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import { AppBar } from '@mui/material';
import Banner from '../../assets/images/banner.png';
import { useLocation } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { ContactFormData } from '../../types/contact';
import { useForm, SubmitHandler } from 'react-hook-form';
import { toast } from 'react-toastify';
import { createContactMessage, getUser } from '../../mutations/mutations';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LegalModal from '../Legal/LegalModal';
import { useQuery } from 'react-query';
import { QueryKeys } from '../../react-query/QueryKeys';
import PrivacyPolicyModal from '../PrivacyPolicyModal';

const transparentAppBarStyles = {
  backgroundColor: CustomColors.sky100,
  backdropFilter: 'blur(10px)',
  transition: 'background-color 0.3s ease',
  boxShadow: 'none',
};

const blueAppBarStyles = {
  backgroundColor: CustomColors.foregroundColor,
  boxShadow: 'none',
};

const Navbar = () => {
  const isSmallScreen = useMediaQuery('(max-width: 600px)');
  const isMediumScreen = useMediaQuery('(max-width: 1200px)');
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [successModal, setSuccessModal] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = useState('');
  const maxCharacterCount = 1000;
  const [legalModalOpen, setLegalModalOpen] = React.useState(false);
  const [privacyPolicyModalOpen, setPrivacyPolicyModalOpen] =
    React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ContactFormData>();

  const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (newValue.length <= maxCharacterCount) {
      setValue('message', newValue);
      setMessage(newValue);
    }
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleSuccessModalClose = () => {
    setSuccessModal(false);
  };

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    try {
      let { message } = data;

      setLoading(true);
      await createContactMessage(user?.sub, user?.name, message);
      toast.success('Contact message sent successfully');
      setLoading(false);
      handleModalClose();
      setSuccessModal(true);
      reset();
      setMessage('');
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const { user } = useAuth0();

  const { data: userData, isLoading } = useQuery(
    QueryKeys.PROFILE,
    () => getUser(user?.sub || ''),
    {
      enabled: !!user,
    }
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [scrolling, setScrolling] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleScroll = () => {
    if (window.scrollY > 50) {
      setScrolling(true);
    } else {
      setScrolling(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <AppBar
        position="sticky"
        sx={scrolling ? blueAppBarStyles : transparentAppBarStyles}
      >
        <Container maxWidth="xl">
          <Grid container padding={2}>
            <Grid
              item
              xs={4}
              md={4}
              sx={{
                display: { xs: 'none', md: 'block' },
              }}
            >
              <Grid
                container
                justifyContent="center"
                onClick={handleModalOpen}
                style={{ cursor: 'pointer' }}
              >
                <Grid item xs={2} md={0.7}>
                  <MailOutlineIcon
                    sx={{
                      fontSize: '22px',
                      color: scrolling
                        ? CustomColors.white
                        : CustomColors.foregroundColor,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      color: scrolling
                        ? CustomColors.white
                        : CustomColors.foregroundColor,
                    }}
                  >
                    Contact
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              xs={6}
              md={4}
              style={{
                display: isSmallScreen ? 'initial' : 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <a onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
                <img
                  src={scrolling ? WhiteLogo : Logo}
                  style={{
                    width: '120px',
                    height: '16px',
                    marginTop: isSmallScreen ? '10px' : '0px',
                  }}
                  alt="BlueSana Logo"
                />
              </a>
            </Grid>
            <Grid
              item
              xs={6}
              md={4}
              style={{
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'end',
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {isSmallScreen ? (
                    <span
                      onClick={handleClick}
                      id="fade-button"
                      aria-controls={open ? 'fade-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? 'true' : undefined}
                    >
                      <MenuIcon
                        sx={{
                          color: scrolling
                            ? CustomColors.white
                            : CustomColors.foregroundColor,
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    </span>
                  ) : (
                    <>
                      <Typography
                        sx={{
                          flexGrow: 1,
                          cursor: 'pointer',
                          marginBottom: '4px',
                          fontSize: '16px',
                        }}
                        id="fade-button"
                        aria-controls={open ? 'fade-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                        color={scrolling ? 'white' : 'secondary'}
                      >
                        Hi, {user?.name}
                      </Typography>
                      <KeyboardArrowDownIcon
                        sx={{
                          marginBottom: '4px',
                          color: scrolling
                            ? CustomColors.white
                            : CustomColors.foregroundColor,
                        }}
                      />
                    </>
                  )}
                </div>
                <Menu
                  id="fade-menu"
                  MenuListProps={{
                    'aria-labelledby': 'fade-button',
                  }}
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  TransitionComponent={Fade}
                  sx={{
                    '& .MuiPopover-paper': {
                      width: '200px',
                      marginTop: '10px',
                      borderRadius: '10px',
                    },
                    '& .MuiMenuItem-root': {
                      fontSize: '14px',
                      background: '#fff',
                      '&:hover': {
                        backgroundColor: CustomColors.primary,
                      },
                    },
                    '& .Mui-selected': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      navigate('/settings');
                      handleClose();
                    }}
                  >
                    Settings
                  </MenuItem>
                  <MenuItem onClick={() => setLegalModalOpen(true)}>
                    Platform User Agreement
                  </MenuItem>
                  <MenuItem onClick={() => setPrivacyPolicyModalOpen(true)}>
                    Privacy Policy
                  </MenuItem>
                  <MenuItem onClick={logout}>Logout</MenuItem>
                </Menu>
              </div>
            </Grid>
          </Grid>
        </Container>
      </AppBar>
      {location.pathname === '/dashboard' && (
        <div
          style={{ position: 'relative', width: '100%' }}
          className="image-container"
        >
          <img
            style={{ width: '100%', height: '400px' }}
            src={Banner}
            alt="Banner"
          />
          <Typography
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              textAlign: 'center',
              transform: 'translate(-50%, -50%)',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
              lineHeight: '1.1',
              fontSize: !isSmallScreen
                ? isMediumScreen
                  ? '40px'
                  : '60px'
                : '30px',
              textTransform: 'uppercase',
            }}
            variant="h1"
            color={'white'}
          >
            Invest in the future
          </Typography>

          <Typography
            style={{
              position: 'absolute',
              top: '80%',
              display: 'block',
              left: '50%',
              textAlign: 'center',
              fontSize: !isSmallScreen ? '28px' : '20px',
              lineHeight: '1.1',
              transform: 'translate(-50%, -50%)',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
              textTransform: 'uppercase',
            }}
            variant="h5"
            color={CustomColors.sky400}
          >
            The Climate Investing Marketplace
          </Typography>
        </div>
      )}

      <Dialog
        open={modalOpen}
        maxWidth={'md'}
        fullWidth={true}
        onClose={handleModalClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Typography variant="h3">Contact Form</Typography>

            <FormControl fullWidth margin="normal">
              <Controller
                name="name"
                control={control}
                defaultValue={user?.name}
                rules={{
                  required: 'Name is required',
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Full Name"
                    variant="outlined"
                    fullWidth
                    disabled
                    error={!!errors.name}
                    helperText={errors.name ? 'Name is required' : ''}
                  />
                )}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Controller
                name="email"
                control={control}
                defaultValue={user?.email}
                rules={{
                  required: 'Email is required',
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    variant="outlined"
                    fullWidth
                    disabled
                    error={!!errors.email}
                    helperText={errors.email ? 'email is required' : ''}
                  />
                )}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Controller
                name="phone"
                control={control}
                defaultValue={userData?.user_metadata?.phone}
                rules={{
                  required: 'Phone is required',
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone"
                    variant="outlined"
                    fullWidth
                    disabled
                    error={!!errors.phone}
                    helperText={errors.phone ? 'Phone is required' : ''}
                  />
                )}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Controller
                name="linkedInProfile"
                control={control}
                defaultValue={userData?.user_metadata?.linkedInProfile}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Linkedin Profile"
                    variant="outlined"
                    fullWidth
                    disabled
                  />
                )}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Controller
                name="message"
                control={control}
                defaultValue={undefined}
                rules={{
                  validate: (value) =>
                    value?.trim() !== '' || 'Message is required',
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Message"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={5}
                    value={message}
                    onChange={handleMessageChange}
                    error={!!errors.message}
                    helperText={errors.message ? errors.message.message : ''}
                  />
                )}
              />
            </FormControl>
            <Typography
              variant="body2"
              color={
                message.length > maxCharacterCount ? 'error' : 'textPrimary'
              }
            >
              {message.length}/{maxCharacterCount} characters
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleModalClose}
              color="secondary"
              variant="outlined"
            >
              Close
            </Button>
            <Button
              type="submit"
              disabled={loading}
              color="secondary"
              variant="contained"
            >
              Submit
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={successModal}
        maxWidth={'sm'}
        fullWidth={true}
        onClose={handleSuccessModalClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <Grid
            container
            direction={'column'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Grid item>
              <CheckCircleIcon
                style={{ color: '#AAD0D4', fontSize: '150px' }}
              />
            </Grid>
            <Grid item>
              We appreciate you taking the time to reach out to us. Your inquiry
              has been received, and we will respond to you as soon as possible.
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSuccessModalClose}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <LegalModal
        open={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
      />

      <PrivacyPolicyModal
        open={privacyPolicyModalOpen}
        onClose={() => setPrivacyPolicyModalOpen(false)}
      />
    </>
  );
};

export default Navbar;
